import { forwardRef } from "react";
import { QRCode } from "antd";
import { formatShootAtDisplay } from "../../utils/bookingFormat";
import { useTranslation } from "../../i18n";
import "./BookingPoster.css";

const QR_SIZE = 112;

const BookingPoster = forwardRef(function BookingPoster({ booking, publicUrl }, ref) {
    const { t } = useTranslation();

    if (!booking) {
        return null;
    }

    const { title, location, shootAt, content, duration, notes } = booking;
    const splitLines = (value) =>
        String(value || "")
            .split(/\r?\n/)
            .map((line) => line.trim())
            .filter(Boolean);
    const contentLines = splitLines(content);
    const notesLines = splitLines(notes);

    return (
        <div ref={ref} className="booking-poster">
            <div className="booking-poster__inner">
                <header className="booking-poster__header">
                    <div className="booking-poster__brand">HEI CREATIVE</div>
                    <div className="booking-poster__doc-title">
                        {t("booking.poster.title")}
                        <span className="booking-poster__doc-title-en">{t("booking.poster.titleEn")}</span>
                    </div>
                    <h1 className="booking-poster__project">
                        {title || t("booking.poster.titlePlaceholder")}
                    </h1>
                </header>

                <section className="booking-poster__body">
                    <div className="booking-poster__row">
                        <div className="booking-poster__row-label">{t("booking.poster.time")}</div>
                        <div className="booking-poster__row-value">
                            {formatShootAtDisplay(shootAt)}
                        </div>
                    </div>
                    {duration ? (
                        <div className="booking-poster__row">
                            <div className="booking-poster__row-label">{t("booking.poster.duration")}</div>
                            <div className="booking-poster__row-value">{duration}</div>
                        </div>
                    ) : null}
                    <div className="booking-poster__row">
                        <div className="booking-poster__row-label">{t("booking.poster.location")}</div>
                        <div className="booking-poster__row-value">{location}</div>
                    </div>
                    {contentLines.length === 1 ? (
                        <div className="booking-poster__row">
                            <div className="booking-poster__row-label">{t("booking.poster.content")}</div>
                            <div className="booking-poster__row-value">{contentLines[0]}</div>
                        </div>
                    ) : contentLines.length > 1 ? (
                        <div className="booking-poster__row booking-poster__row--block">
                            <div className="booking-poster__row-label">{t("booking.poster.content")}</div>
                            <div className="booking-poster__content">
                                {contentLines.map((line, index) => (
                                    <div key={`content-${index}`} className="booking-poster__content-line">
                                        {line}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : null}
                    {notesLines.length > 0 ? (
                        <div className="booking-poster__row booking-poster__row--block">
                            <div className="booking-poster__row-label">{t("booking.poster.notes")}</div>
                            <div className="booking-poster__content">
                                {notesLines.map((line, index) => (
                                    <div key={`notes-${index}`} className="booking-poster__content-line">
                                        {line}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : null}
                </section>

                <footer className="booking-poster__footer">
                    <div className="booking-poster__qr-row">
                        <div className="booking-poster__qr-wrapper">
                            <div className="booking-poster__qr-inner">
                                {publicUrl ? (
                                    <QRCode
                                        value={publicUrl}
                                        size={QR_SIZE}
                                        color="#1d1d1f"
                                        bgColor="#ffffff"
                                        bordered={false}
                                        errorLevel="M"
                                    />
                                ) : null}
                            </div>
                        </div>
                        <div className="booking-poster__qr-text">
                            <div>{t("booking.poster.scanZh")}</div>
                            <div>{t("booking.poster.scanEn")}</div>
                        </div>
                    </div>

                    <div className="booking-poster__divider" />

                    <div className="booking-poster__website">HEICREATIVE.CO.UK</div>
                    <span className="booking-poster__local-time">{t("booking.poster.localTime")}</span>

                </footer>
            </div>
        </div>
    );
});

export default BookingPoster;
